import { useEffect, useState } from 'react'

// The three widths the design changes shape at. P6-328b (GN-1b).
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE DESIGN SWAPS MARKUP, NOT JUST STYLE, SO THE WIDTH IS KNOWN TO THE COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
//
// A phone's box score is a column of BUTTONS (a tap opens the full line) with four
// figures; from 720px it is a column of plain rows with twelve. The compact hero is a
// stack of two team rows; from 900px it is a head-to-head with leaders and stat bars. From
// 1000px the box score stays open beside the plays and there is no segment control at all.
// Those are different elements, not one element restyled, and rendering both and hiding
// one with CSS would put every name and score into the document twice — for a screen reader,
// for `getByText`, and for anybody who selects text.
//
// So the component asks which it is. `matchMedia` on the viewport, not a measured container,
// because the design's own widths are the FRAME's (320, 375, 768, 1280) and the frame is the
// viewport on this site.
//
// ⚠️ WITH NO `matchMedia` (jsdom has none) THE ANSWER IS "NARROW". That is the fail-safe
// direction: the phone layout is the one every width can draw and the one nothing scrolls
// sideways in, and it is what the design calls the main case.

/** From here the box score shows every column inline. */
export const BOX_WIDE_PX = 720
/**
 * From here a LIVE card draws the five on the floor. P6-330b. Design flag 1: *"The five on the floor moved off phone
 * cards. At 320-375 they need a sixth row and crowd out the score. Cards show the faces only when the frame is 720
 * px or wider."* It is the box score's number and a different decision, which is why it is named on its own.
 */
export const FLOOR_WIDE_PX = 720
/** From here the hero is the head-to-head. */
export const HERO_WIDE_PX = 900
/** From here the box score stays open beside the plays. */
export const GAME_WIDE_PX = 1000
/** From here the standings show both conferences side by side, and below it one at a time with a segment control. (P6-329c) */
export const STANDINGS_WIDE_PX = 900
/** From here a team's roster adds the MIN and FG% columns. (P6-329c) */
export const ROSTER_WIDE_PX = 560

function listFor(px) {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(`(min-width: ${px}px)`)
    : null
}

/** Below this the box score drops each player's initial and shows the surname alone (the design's `fw < 360`). P6-343. */
export const SURNAME_ONLY_BELOW_PX = 360

/**
 * Is the viewport NARROWER than `px`, and does it stay true as the window is resized? P6-343.
 *
 * 🔴 THE FAIL-SAFE IS THE OPPOSITE OF `useMinWidth`'s. With no `matchMedia` (jsdom, a very old browser) this answers FALSE,
 * "not narrower": the thing it gates is a cosmetic shortening (`J. Brunson` becomes `Brunson`), and the full form is the one
 * that is always right and never misleading. `useMinWidth` answers false too and calls it "narrow", which is right for a
 * layout and would be wrong here: `!useMinWidth(360)` would shorten every name wherever a width cannot be asked.
 * The query is `max-width` just under `px`, so 359.98 is below 360 and 360 is not.
 */
export function useBelowWidth(px) {
  const [matches, setMatches] = useState(() => belowList(px)?.matches ?? false)
  useEffect(() => {
    const list = belowList(px)
    if (list === null) return undefined
    const sync = () => setMatches(list.matches)
    sync()
    if (typeof list.addEventListener === 'function') {
      list.addEventListener('change', sync)
      return () => list.removeEventListener('change', sync)
    }
    list.addListener?.(sync)
    return () => list.removeListener?.(sync)
  }, [px])
  return matches
}

function belowList(px) {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(`(max-width: ${px - 0.02}px)`)
    : null
}

/** Is the viewport at least `px` wide, and does it stay true as the window is resized? */
export function useMinWidth(px) {
  const [matches, setMatches] = useState(() => listFor(px)?.matches ?? false)
  useEffect(() => {
    const list = listFor(px)
    if (list === null) return undefined
    const sync = () => setMatches(list.matches)
    sync()
    // Older Safari has `addListener` and no `addEventListener` on a MediaQueryList.
    if (typeof list.addEventListener === 'function') {
      list.addEventListener('change', sync)
      return () => list.removeEventListener('change', sync)
    }
    list.addListener?.(sync)
    return () => list.removeListener?.(sync)
  }, [px])
  return matches
}
